import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    alpha,
    Fade,
    useTheme,
    useMediaQuery,
    Chip
} from '@mui/material'
import {
    ArrowForward,
    Home,
    Palette,
    Lightbulb,
    ShoppingBag,
    Brush
} from '@mui/icons-material'
import TitleSection from '@components/TitleSection.jsx'

const ServicesOverviewPage = () => {
    const theme = useTheme()

    const [isVisible, setIsVisible] = useState(false)
    const [backgroundImageIndex, setBackgroundImageIndex] = useState(0)

    const allImages = []

    useEffect(() => {
        setIsVisible(true)
        if (allImages.length > 0) {
            const interval = setInterval(() => {
                setBackgroundImageIndex(prev => (prev + 1) % allImages.length)
            }, 7000)
            return () => clearInterval(interval)
        }
    }, [allImages.length])

    const services = [

        {
            id: 'interior-design-styling',
            icon: <Palette sx={{fontSize: 40}}/>,
            title: 'Interior Design',
            description: 'Bespoke interiors blending aesthetics with functionality for daily life.',
            features: ['Space Planning', 'Color Palettes', 'Furniture', 'Art Curation'],
            investment: 'From $10,000',
            timeline: '6 weeks - 6 months',
            gradient: 'linear-gradient(70deg, #F9F7F4 0%, #E5DDD4 100%)',
            popular: true

        },
        {
            id: 'space-consultations',
            icon: <Lightbulb sx={{fontSize: 40}}/>,
            title: 'Home Consultations',
            description: 'Professional insights to maximize functionality and aesthetic appeal.',
            features: ['Space Analysis', 'Layout Optimization', 'Storage Solutions', 'Style Enhancement'],
            investment: 'From $450',
            timeline: '2-4 hours',
            gradient: 'linear-gradient(to right, #F8F6F2 0%, #E7E0D9 100%)'

        },
        {
            id: 'personal-shopping',
            icon: <ShoppingBag sx={{fontSize: 40}}/>,
            title: 'Personal Shopping',
            description: 'Curated sourcing of unique pieces that complement your space.',
            features: ['Trade Access', 'Quality Assessment', 'Delivery', 'Style Curation'],
            investment: 'From $750',
            timeline: '2-6 weeks',
            gradient: 'linear-gradient(120deg, #F4F2EF 0%, #DFD7CF 100%)',
            featured: true
        },
        {
            id: 'digital-design',
            icon: <Brush sx={{fontSize: 40}}/>,
            title: 'Digital Design',
            description: 'Compelling digital presence capturing your essence and story.',
            features: ['Brand Strategy', 'Website Design', 'Social Media', 'Marketing'],
            investment: 'From $3,500',
            timeline: '4-12 weeks',
            gradient: 'linear-gradient(to right, #F1EEEA 0%, #E2DAD2 100%)'
        },
        {
            id: 'home-staging',
            icon: <Home sx={{fontSize: 40}}/>,
            title: 'Home Staging',
            description: 'Transform properties into irresistible homes that create emotional connections.',
            features: ['Property Assessment', 'Custom Furniture', 'Photography Prep', 'Virtual Staging'],
            investment: 'From $2,500',
            timeline: '2-5 days',
            gradient: 'linear-gradient(to bottom, #FBFAF8 0%, #EFEAE4 100%)',
            popular: true
        },
    ]

  const colors = {
    primary:    '#120101',
    secondary:  '#2C3E50',
    accent:     '#0e0202',
    background: '#FAF7F2',
    text:       '#2C2C2C',
    lightText:  '#fdfdfd'
  }


    return (

     <Box sx={{minHeight: '100vh', bgcolor: '#FDFDFB'}}>
       <TitleSection/>

            <Container maxWidth="xl" sx={{py: {xs: 6, md: 10}}}>
                <Box sx={{textAlign: 'center', mb: 10}}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: {xs: '2.5rem', md: '3.5rem', lg: '4.5rem'},
                            fontWeight: 300,
                            letterSpacing: '-0.02em',
                            color: '#1a1a1a',
                            mb: 3,
                            fontFamily: '"Cormorant Garamond", serif',
                            lineHeight: 1.1
                        }}
                    >
                        Design Services
                    </Typography>
                </Box>

                <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                    {services.map((service, index) => (
                        <Grid item xs={12} md={6} lg={4} sx={{padding: 5}} key={service.id}>
                            <Fade in={isVisible} timeout={1000 + index * 100}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: '25px',
                                        border: '1px solid rgba(184, 153, 104, 0.08)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                        textShadow: '0 1px 2px rgba(255, 255, 255, 0.1)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 12px 40px rgba(26, 26, 26, 0.5)'
                                        }
                                    }}
                                >
                                    {service.popular && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 12,
                                            right: 12,
                                            bgcolor: '#373232',
                                            color: 'white',
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: '8px',
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            zIndex: 2
                                        }}>
                                            Popular
                                        </Box>
                                    )}
                                    {service.featured && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 12,
                                            right: 12,
                                            bgcolor: 'rgba(62,54,50,0.68)',
                                            color: 'white',
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: '8px',
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            zIndex: 2
                                        }}>
                                            Featured
                                        </Box>
                                    )}

                                    <Box sx={{
                                        background: service.gradient,
                                        color: 'white',
                                        p: 5,
                                        textAlign: 'center'
                                    }}>

                                        <Typography variant="h6" sx={{
                                            fontFamily: '"Cormorant Garamond", serif',
                                            fontWeight: 400,
                                            fontSize: {xs: '1.7rem', md: '2.0rem', lg: '2.35rem'},

                                            lineHeight: 1.0,
                                        }}>
                                            {service.title}
                                        </Typography>
                                    </Box>

                                    <CardContent sx={{
                                        p: 5,
                                        textAlign: 'center',
                                        flexGrow: 1,
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#666',
                                                lineHeight: 1.5,
                                                mb: 2,
                                                flexGrow: 1
                                            }}
                                        >
                                            {service.description}
                                        </Typography>

                                        <Box sx={{textAlign: 'center', mb: 2}}>
                                            <Box sx={{
                                                display: 'inline-flex',
                                                flexWrap: 'wrap',
                                                gap: 0.3,
                                                justifyContent: 'center'
                                            }}>
                                                {service.features.slice(0, 3).map((feature, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={feature}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: alpha('#B89968', 0.1),
                                                            color: '#614f33',
                                                            fontSize: '0.7rem',
                                                            height: 22,
                                                            '&:hover': {
                                                                bgcolor: alpha('#B89968', 0.15),
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>


                                        <Button
                                            component={Link}
                                            to={`${service.id}`}
                                            variant="outlined"
                                            fullWidth
                                            endIcon={<ArrowForward/>}
                                            sx={{
                                                borderColor: '#614f33',
                                                color: '#352d18',
                                                borderRadius: '20px',
                                                py: 1,
                                                fontSize: '0.9rem',
                                                '&:hover': {
                                                    bgcolor: alpha('#B89968', 0.08)
                                                }
                                            }}
                                        >
                                            Explore
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Fade>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            <Box sx={{
                background: `
            linear-gradient(135deg, rgba(184, 153, 104, 0.08) 0%, transparent 50%),
            linear-gradient(45deg, transparent 30%, rgba(184, 153, 104, 0.05) 100%)
          `,
                py: {xs: 6, md: 8}
            }}>
                <Container maxWidth="lg">
                    <Fade in={isVisible} timeout={2000}>
                        <Box sx={{textAlign: 'center'}}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 300,
                                    color: '#120101',
                                    mb: 2,
                                    fontSize: {xs: '1.5rem', md: '2rem'},
                                    fontFamily: '"Cormorant Garamond", serif'
                                }}
                            >
                                Begin Your Design Journey
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: alpha('#1c0101', 0.7),
                                    mb: 4,
                                    lineHeight: 1.6,
                                    maxWidth: 500,
                                    mx: 'auto'
                                }}
                            >
                                Let's transform your space into something extraordinary.
                            </Typography>
                            <Button
                                component={Link}
                                to="/contact"
                                variant="outlined"
                                size="large"
                                endIcon={<ArrowForward/>}
                                sx={{
                                    borderRadius: '50px',
                                    px: 5,
                                    py: 1.5,
                                    border: '1px solid rgba(255, 255, 255, 0.4)',
                                    color: '#1a1a1a',
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(25px)',
                                    WebkitBackdropFilter: 'blur(25px)',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                    textShadow: '0 1px 2px rgba(255, 255, 255, 0.5)',
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.25)',
                                        backdropFilter: 'blur(30px)',
                                        WebkitBackdropFilter: 'blur(30px)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                                        borderColor: 'rgba(255, 255, 255, 0.6)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Start Project
                            </Button>
                        </Box>
                    </Fade>
                </Container>
            </Box>
        </Box>

    )
}

export default ServicesOverviewPage